import React, { useState } from 'react';
import { practiceInfo, patientForms } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Download, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Thank you for your inquiry. We will contact you within 24 hours.');
    
    // Reset form after delay
    setTimeout(() => {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        interest: '',
        message: ''
      });
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <section id="contact" className="section-padding bg-[#fffef8]">
      <div className="container-custom">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="gold-accent mx-auto mb-6"></div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#2d2d2d] mb-6">
            Begin Your Journey
          </h2>
          <p className="text-[#5a5a5a] text-lg leading-relaxed">
            Ready to experience healthcare the way it should be? Schedule a 
            complimentary consultation to learn how Exquisite Concierge Medicine 
            can transform your care.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact form */}
          <div className="bg-[#f8f7f2] p-8 lg:p-12">
            <h3 className="font-serif text-2xl text-[#2d2d2d] mb-6">
              Schedule a Consultation
            </h3>
            
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle size={48} className="text-[#c9a962] mb-4" />
                <h4 className="font-serif text-xl text-[#2d2d2d] mb-2">
                  Thank You
                </h4>
                <p className="text-[#5a5a5a]">
                  We have received your inquiry and will contact you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-[#5a5a5a] mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#5a5a5a] mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Your last name"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-[#5a5a5a] mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#5a5a5a] mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="(555) 000-0000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#5a5a5a] mb-2">I'm interested in *</label>
                  <select
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select an option</option>
                    <option value="essential">Essential Membership</option>
                    <option value="premier">Premier Membership</option>
                    <option value="executive">Executive Membership</option>
                    <option value="learn-more">Learning More About Concierge Medicine</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-[#5a5a5a] mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="form-input resize-none"
                    placeholder="Tell us about your healthcare needs or any questions you have..."
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-solid w-full"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Inquiry
                      <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Contact info & forms */}
          <div>
            {/* Contact details */}
            <div className="mb-12">
              <h3 className="font-serif text-2xl text-[#2d2d2d] mb-6">
                Contact Information
              </h3>
              <div className="space-y-6">
                <a href={`tel:${practiceInfo.phone}`} className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[#f8f7f2] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1a1a1a] transition-colors">
                    <Phone size={20} className="text-[#c9a962]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8a8a8a] mb-1">Phone</p>
                    <p className="text-[#2d2d2d] group-hover:text-[#c9a962] transition-colors">
                      {practiceInfo.phone}
                    </p>
                  </div>
                </a>
                
                <a href={`mailto:${practiceInfo.email}`} className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-[#f8f7f2] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1a1a1a] transition-colors">
                    <Mail size={20} className="text-[#c9a962]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8a8a8a] mb-1">Email</p>
                    <p className="text-[#2d2d2d] group-hover:text-[#c9a962] transition-colors">
                      {practiceInfo.email}
                    </p>
                  </div>
                </a>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#f8f7f2] flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[#c9a962]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8a8a8a] mb-1">Location</p>
                    <p className="text-[#2d2d2d]">{practiceInfo.address}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#f8f7f2] flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-[#c9a962]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8a8a8a] mb-1">Office Hours</p>
                    <p className="text-[#2d2d2d]">{practiceInfo.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Patient forms */}
            <div>
              <h3 className="font-serif text-2xl text-[#2d2d2d] mb-6">
                New Patient Forms
              </h3>
              <p className="text-[#5a5a5a] text-sm mb-6">
                Download and complete these forms prior to your first visit to 
                expedite your onboarding process.
              </p>
              <div className="space-y-3">
                {patientForms.map((form) => (
                  <button
                    key={form.id}
                    className="w-full flex items-center justify-between p-4 bg-[#f8f7f2] hover:bg-[#1a1a1a] group transition-colors"
                    onClick={() => toast.info('Form download functionality will be available with backend integration.')}
                  >
                    <div className="text-left">
                      <p className="text-[#2d2d2d] text-sm font-medium group-hover:text-[#fffef8] transition-colors">
                        {form.name}
                      </p>
                      <p className="text-[#8a8a8a] text-xs group-hover:text-[#b8b8b8] transition-colors">
                        {form.description}
                      </p>
                    </div>
                    <Download size={18} className="text-[#c9a962] flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
